import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, auth } from "../lib/api";
import { toast } from "sonner";
import BrandHeader from "../components/BrandHeader";
import SessionDetailDialog from "../components/SessionDetailDialog";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "../components/ui/table";
import { ArrowLeft, Loader2, RefreshCw } from "lucide-react";

const TERMS = ["Term 1", "Term 2", "Term 3", "Term 4"];

export default function SchoolLogsPage() {
  const { schoolName: rawName } = useParams();
  const schoolName = decodeURIComponent(rawName || "");
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [term, setTerm] = useState("__all__");
  const [showDeleted, setShowDeleted] = useState(false);
  const [detailId, setDetailId] = useState(null);

  useEffect(() => {
    if (!auth.isAuthed()) nav("/admin/login");
  }, [nav]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { school: schoolName };
      if (term !== "__all__") params.term = term;
      if (showDeleted) params.deleted = "true";
      const r = await api.get("/admin/sessions", { params });
      setRows(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load logs");
    } finally { setLoading(false); }
  }, [schoolName, term, showDeleted]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/admin/sessions/${id}`);
      toast.success("Session moved to Recently Deleted");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  const handleRestore = async (id) => {
    try {
      await api.post(`/admin/sessions/${id}/restore`);
      toast.success("Session restored");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Restore failed"); }
  };

  const heading = useMemo(() => `${schoolName} — Session Logs`, [schoolName]);

  return (
    <div className="min-h-screen bg-[#FAF7F2] pb-16">
      <BrandHeader
        right={
          <Link to="/admin" className="text-xs text-neutral-500 hover:text-neutral-800 inline-flex items-center gap-1" data-testid="link-back-admin">
            <ArrowLeft className="w-3 h-3" /> Admin
          </Link>
        }
      />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <div>
          <h1 className="font-display text-3xl font-semibold text-neutral-900 tracking-tight" data-testid="school-logs-heading">
            {heading}
          </h1>
          <p className="text-sm text-neutral-500 mt-1">
            Newest first. Filter by term, or toggle Recently Deleted to see soft-deleted logs.
          </p>
        </div>

        <section className="rounded-xl bg-white border border-neutral-200 p-4 sm:p-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4 items-end">
            <div>
              <Label className="text-sm text-neutral-700 mb-1 block">Term</Label>
              <Select value={term} onValueChange={setTerm}>
                <SelectTrigger className="h-11" data-testid="school-logs-term">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All terms</SelectItem>
                  {TERMS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2 flex items-center gap-3">
              <button
                onClick={() => setShowDeleted((v) => !v)}
                data-testid="btn-toggle-deleted"
                className={
                  "h-11 px-4 rounded-md border text-sm font-medium " +
                  (showDeleted
                    ? "bg-red-50 border-red-300 text-red-800"
                    : "bg-white border-neutral-300 text-neutral-700 hover:border-neutral-500")
                }
              >
                {showDeleted ? "Showing: Recently Deleted" : "Show Recently Deleted"}
              </button>
              <button
                onClick={load}
                className="h-11 px-3 text-xs text-neutral-500 hover:text-neutral-800 inline-flex items-center"
                data-testid="btn-school-logs-refresh"
              >
                <RefreshCw className={`w-3 h-3 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
              </button>
            </div>
          </div>

          <div className="text-sm text-neutral-600 mb-2">
            {loading ? (
              <><Loader2 className="w-3 h-3 inline animate-spin mr-1" /> Loading…</>
            ) : (
              <><b className="text-neutral-900">{rows.length}</b> session{rows.length === 1 ? "" : "s"}{showDeleted ? " (deleted)" : ""}</>
            )}
          </div>

          {rows.length === 0 && !loading ? (
            <div className="text-sm text-neutral-500 py-10 text-center" data-testid="school-logs-empty">
              No sessions for this school{term !== "__all__" ? ` in ${term}` : ""}{showDeleted ? " in Recently Deleted" : ""}.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Term · Week</TableHead>
                    <TableHead>Class · Year</TableHead>
                    <TableHead>Teacher</TableHead>
                    <TableHead className="text-right">CPI Total</TableHead>
                    <TableHead>Attendance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow
                      key={r.id}
                      className="cursor-pointer hover:bg-neutral-50"
                      onClick={() => setDetailId(r.id)}
                      data-testid={`school-log-row-${r.id}`}
                    >
                      <TableCell className="whitespace-nowrap">{r.date}</TableCell>
                      <TableCell className="whitespace-nowrap">
                        <div>{r.term}</div>
                        <div className="text-xs text-neutral-500">{r.session_week}</div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <div>{r.class_name}</div>
                        <div className="text-xs text-neutral-500">{r.year_level}</div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">{r.teacher_name}</TableCell>
                      <TableCell className={`text-right whitespace-nowrap font-semibold ${r.cpi_total == null ? "text-neutral-400" : "text-[#2D5A4F]"}`}>
                        {r.cpi_total == null ? "N/A" : `${r.cpi_total}/100`}
                      </TableCell>
                      <TableCell className={`whitespace-nowrap ${r.attendance_status !== "Valid" ? "text-amber-800" : "text-neutral-700"}`}>
                        {r.attendance_status}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </section>
      </main>

      <SessionDetailDialog
        sessionId={detailId}
        onClose={() => setDetailId(null)}
        onDelete={showDeleted ? undefined : handleDelete}
        onRestore={showDeleted ? handleRestore : undefined}
      />
    </div>
  );
}
